const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

function setFocusableState(container, enabled) {
  const focusableSelectors = [
    "a[href]",
    "button",
    "input",
    "textarea",
    "select",
    "iframe",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  const elements = container.querySelectorAll(focusableSelectors);

  elements.forEach((el) => {
    if (enabled) {
      // restore original tabindex if we stored one
      if (enabled) {
        if (el.dataset.originalTabindex !== undefined) {
          el.setAttribute("tabindex", el.dataset.originalTabindex);
          delete el.dataset.originalTabindex;
        } else {
          el.removeAttribute("tabindex"); // fine
        }
      } else {
        if (!el.hasAttribute("tabindex")) {
          el.dataset.originalTabindex = "";
        } else {
          el.dataset.originalTabindex = el.getAttribute("tabindex");
        }
        el.setAttribute("tabindex", "-1");
      }
    } else {
      // store current tabindex (if any)
      if (el.hasAttribute("tabindex")) {
        el.dataset.originalTabindex = el.getAttribute("tabindex");
      }
      el.setAttribute("tabindex", "-1");
    }
  });
}

export default function initAccordions() {
  const accordions = document.querySelectorAll("[data-accordion]");
  if (!accordions.length) return;

  accordions.forEach((accordion) => {
    accordion.addEventListener("click", (e) => {
      const button = e.target.closest("[data-accordion-toggle]");
      if (!(button instanceof HTMLElement)) return;

      const panelId = button.getAttribute("aria-controls");
      if (!panelId) return;

      const panel = accordion.querySelector(`#${panelId}`);
      if (!panel) return;

      const expanded = button.getAttribute("aria-expanded") === "true";

      if (expanded) {
        closePanel(button, panel);
      } else {
        accordion
          .querySelectorAll(".sz-accordion__trigger[aria-expanded='true']")
          .forEach((btn) => {
            const id = btn.getAttribute("aria-controls");
            const openPanel = id ? document.getElementById(id) : null;
            if (btn !== button && openPanel) closePanel(btn, openPanel);
          });

        openPanel(button, panel);
      }
    });
    accordion.querySelectorAll(".sz-accordion__panel").forEach((panel) => {
      if (panel.getAttribute("aria-hidden") === "true") {
        setFocusableState(panel, false);
      }
    });

    /* keyboard navigation */
    accordion.addEventListener("keydown", handleKeyboard);
  });

  /* deep linking */
  openFromHash();
  window.addEventListener("hashchange", openFromHash);
}

/* ----------------------------------------
open panel
----------------------------------------- */
function openPanel(button, panel) {
  button.setAttribute("aria-expanded", "true");
  panel.classList.add("is-open");
  panel.setAttribute("aria-hidden", "false");
  setFocusableState(panel, true);

  const item = button.closest(".sz-accordion__item");
  if (item && item.id) {
    history.replaceState(null, "", `#${item.id}`);
  }

  if (!prefersReducedMotion) {
    panel.style.maxHeight = "0px";
    panel.offsetHeight;
    setTimeout(() => {
      if (panel.classList.contains("is-open")) {
        panel.style.maxHeight = panel.scrollHeight + "px";
      }
    }, 100);

    panel.addEventListener(
      "transitionend",
      () => {
        if (panel.classList.contains("is-open")) {
          panel.style.maxHeight = "none";
        }
      },
      { once: true },
    );
  } else {
    panel.style.maxHeight = null;
  }
}

/* ----------------------------------------
close panel
----------------------------------------- */
function closePanel(button, panel) {
  button.setAttribute("aria-expanded", "false");
  panel.classList.remove("is-open");
  panel.setAttribute("aria-hidden", "true");
  setFocusableState(panel, false);

  if (!prefersReducedMotion) {
    panel.style.maxHeight = panel.scrollHeight + "px";
    panel.offsetHeight;
    panel.style.maxHeight = "0px";
  } else {
    panel.style.maxHeight = null;
  }
}
/* ----------------------------------------
keyboard navigation
----------------------------------------- */

function handleKeyboard(e) {
  const target = e.target;

  if (!(target instanceof HTMLElement)) return;
  if (!target.matches("[data-accordion-toggle]")) return;

  const accordion = target.closest("[data-accordion]");
  if (!accordion) return;

  const buttons = Array.from(
    accordion.querySelectorAll("[data-accordion-toggle]"),
  );

  const index = buttons.indexOf(target);
  if (index === -1) return;

  switch (e.key) {
    case "ArrowDown":
      e.preventDefault();
      buttons[(index + 1) % buttons.length].focus();
      break;

    case "ArrowUp":
      e.preventDefault();
      buttons[(index - 1 + buttons.length) % buttons.length].focus();
      break;

    case "Home":
      e.preventDefault();
      buttons[0].focus();
      break;

    case "End":
      e.preventDefault();
      buttons[buttons.length - 1].focus();
      break;

    case "Enter":
    case " ":
      e.preventDefault();
      target.click();
      break;
  }
}
/* ----------------------------------------
deep linking
----------------------------------------- */
function openFromHash() {
  const hash = window.location.hash;
  if (!hash) return;

  const item = document.querySelector(hash);
  if (!(item instanceof HTMLElement)) return;

  const btn = item.querySelector("[data-accordion-toggle]");
  if (!(btn instanceof HTMLElement)) return;

  const panelId = btn.getAttribute("aria-controls");
  const panel = panelId ? document.getElementById(panelId) : null;
  if (!panel) return;

  const accordion = btn.closest("[data-accordion]");
  if (!accordion) return;

  // close others
  accordion
    .querySelectorAll(".sz-accordion__trigger[aria-expanded='true']")
    .forEach((openBtn) => {
      if (openBtn !== btn) {
        const id = openBtn.getAttribute("aria-controls");
        const openPanelEl = id ? document.getElementById(id) : null;
        if (openPanelEl) closePanel(openBtn, openPanelEl);
      }
    });

  // open target if needed
  if (btn.getAttribute("aria-expanded") !== "true") {
    openPanel(btn, panel);
  }

  setTimeout(() => {
    item.scrollIntoView({
      block: "start",
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  }, 100);
}
